<template>
  <ion-page>
    <ion-header :translucent="true">
      <ion-toolbar class="custom-toolbar">
        <ion-buttons slot="start">
          <ion-menu-button color="light"></ion-menu-button>
        </ion-buttons>
        <ion-title class="custom-title">Leave Approvals</ion-title>
        <ion-buttons slot="end">
          <ion-button @click="fetchPendingApprovals" color="light" class="refresh-btn">
            <ion-icon slot="icon-only" :icon="refreshOutline"></ion-icon>
          </ion-button>
        </ion-buttons>
      </ion-toolbar>
    </ion-header>

    <ion-content :fullscreen="true" class="approval-content">
      <div class="approval-container">
        
        <!-- Summary Box -->
        <div v-if="!loading && pendingList.length > 0" class="pending-summary">
          <ion-icon :icon="notificationsOutline" class="summary-icon"></ion-icon>
          <span>You have <strong>{{ pendingList.length }}</strong> pending leave request(s) requiring your review.</span>
        </div>

        <!-- Loader -->
        <div v-if="loading" class="loading-state">
          <ion-spinner name="crescent" color="primary"></ion-spinner>
          <span>Fetching pending requisitions...</span>
        </div>

        <!-- Approvals List -->
        <div v-else-if="pendingList.length > 0" class="approvals-list">
          <div 
            v-for="req in pendingList" 
            :key="req.id" 
            class="approval-card"
            :class="pushFocusClass(req.id)"
            :data-push-id="req.id"
          >
            <!-- Card Accent line -->
            <div class="card-accent"></div>

            <div class="card-header-row">
              <div class="employee-info">
                <div class="avatar-badge">
                  {{ getInitials(req.user ? req.user.name : 'Employee') }}
                </div>
                <div>
                  <h3 class="employee-name">{{ req.user ? req.user.name : 'Unknown Employee' }}</h3>
                  <span class="employee-sub">Requesting Leave</span>
                </div>
              </div>
              <div class="leave-type-badge">
                <ion-icon :icon="getLeaveIcon(req.leave_type ? req.leave_type.title : '')" class="type-icon"></ion-icon>
                <span>{{ req.leave_type ? req.leave_type.title : 'Leave' }}</span>
              </div>
            </div>

            <div class="card-body">
              <!-- Bento Dates Layout -->
              <div class="dates-bento">
                <div class="date-col">
                  <span class="date-label">START DATE</span>
                  <span class="date-val">{{ formatDate(req.start_date) }}</span>
                </div>
                <div class="duration-arrow">
                  <ion-icon :icon="arrowForwardOutline"></ion-icon>
                  <span class="duration-pill">{{ req.num_of_days }} Days</span>
                </div>
                <div class="date-col">
                  <span class="date-label">END DATE</span>
                  <span class="date-val">{{ formatDate(req.end_date) }}</span>
                </div>
              </div>

              <!-- Reason -->
              <div class="reason-box">
                <span class="box-title">Reason for Leave:</span>
                <p class="reason-text">"{{ req.reason || 'No reason provided' }}"</p>
              </div>

              <!-- Attachment attachment-url -->
              <div v-if="req.attachment_url" class="attachment-section">
                <a :href="req.attachment_url" target="_blank" class="btn-attachment">
                  <ion-icon :icon="documentAttachOutline"></ion-icon>
                  <span>View Attached Document</span>
                </a>
              </div>
            </div>

            <!-- Action Buttons Row -->
            <div class="card-actions">
              <button class="btn-action btn-decline" @click="promptDecline(req.id)">
                <ion-icon :icon="closeOutline"></ion-icon>
                <span>Decline</span>
              </button>
              <button class="btn-action btn-approve" @click="approve(req.id)">
                <ion-icon :icon="checkmarkOutline"></ion-icon>
                <span>Approve Request</span>
              </button>
            </div>
          </div>
        </div>

        <!-- Empty State Redesign -->
        <div v-else class="empty-state">
          <div class="empty-icon-wrapper">
            <ion-icon :icon="checkmarkDoneCircleOutline" class="empty-icon"></ion-icon>
          </div>
          <h2>All Caught Up!</h2>
          <p>No pending leave approvals. Any future team leave applications requiring your verification will appear here.</p>
        </div>

      </div>
    </ion-content>
  </ion-page>
</template>

<script>
/* eslint-disable */
import { 
  IonButtons, 
  IonContent, 
  IonHeader, 
  IonMenuButton, 
  IonPage, 
  IonTitle, 
  IonToolbar, 
  IonButton, 
  IonIcon, 
  alertController,
  IonSpinner
} from '@ionic/vue';
import { 
  documentAttachOutline,
  refreshOutline,
  notificationsOutline,
  checkmarkOutline,
  closeOutline,
  arrowForwardOutline,
  medicalOutline,
  happyOutline,
  airplaneOutline,
  documentTextOutline,
  checkmarkDoneCircleOutline
} from 'ionicons/icons';
import Loading from '../../Loading';
import { pushFocus } from '../../pushNotifications';

export default {
  name: 'LeaveApprovals',
  mixins: [pushFocus],
  components: {
    IonButtons,
    IonContent,
    IonHeader,
    IonMenuButton,
    IonPage,
    IonTitle,
    IonToolbar,
    IonButton,
    IonIcon,
    IonSpinner
  },
  data() {
    return {
      pendingList: [],
      loading: true,
      // Icons
      documentAttachOutline,
      refreshOutline,
      notificationsOutline,
      checkmarkOutline,
      closeOutline,
      arrowForwardOutline,
      checkmarkDoneCircleOutline
    }
  },
  created() {
    this.fetchPendingApprovals();
  },
  methods: {
    reloadForPush() {
      this.fetchPendingApprovals();
    },
    formatDate(dateStr) {
      if (!dateStr) return '';
      try {
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
      } catch (e) {
        return dateStr;
      }
    },
    getInitials(name) {
      if (!name) return 'EE';
      const parts = name.split(' ');
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
      }
      return name.substring(0, 2).toUpperCase();
    },
    getLeaveIcon(title) {
      if (!title) return documentTextOutline;
      const t = title.toLowerCase();
      if (t.includes('sick')) return medicalOutline;
      if (t.includes('casual')) return happyOutline;
      if (t.includes('annual') || t.includes('earn')) return airplaneOutline;
      return documentTextOutline;
    },
    async presentAlert(title, message) {
      const alert = await alertController.create({
        header: title,
        message: message,
        buttons: ['OK'],
      });
      await alert.present();
    },
    fetchPendingApprovals() {
      this.loading = true;
      const data = {
        username: window.localStorage.username,
        password: window.localStorage.password,
      };

      this.$http.post('hr/pendingLeaveApprovals', data, { skipLoading: true })
        .then(response => {
          if (response.data.status) {
            this.pendingList = response.data.pending;
          }
          this.loading = false;
          // Opened from a notification: show that request.
          this.focusPushCard('This leave request has already been handled.');
        })
        .catch(err => {
          console.error(err);
          this.loading = false;
        });
    },
    async approve(requisitionId) {
      const confirmAlert = await alertController.create({
        header: 'Confirm Approval',
        message: 'Are you sure you want to approve this leave request?',
        buttons: [
          { text: 'Cancel', role: 'cancel' },
          {
            text: 'Approve',
            handler: async () => {
              const loader = await Loading.presentLoading('Approving...');
              const data = {
                username: window.localStorage.username,
                password: window.localStorage.password,
                requisition_id: requisitionId,
              };

              this.$http.post('hr/approveLeaveRequisition', data)
                .then(response => {
                  loader.dismiss();
                  if (response.data.status) {
                    this.presentAlert('Success', response.data.message);
                    this.fetchPendingApprovals();
                  } else {
                    this.presentAlert('Failed', response.data.message);
                  }
                })
                .catch(err => {
                  loader.dismiss();
                });
            }
          }
        ]
      });
      await confirmAlert.present();
    },
    async promptDecline(requisitionId) {
      const promptAlert = await alertController.create({
        header: 'Decline Leave Request',
        inputs: [
          {
            name: 'decline_reason',
            type: 'text',
            placeholder: 'Reason for declining *'
          }
        ],
        buttons: [
          { text: 'Cancel', role: 'cancel' },
          {
            text: 'Decline',
            handler: async (alertData) => {
              if (!alertData.decline_reason || !alertData.decline_reason.trim()) {
                this.presentAlert('Validation Error', 'Decline reason is required.');
                return false;
              }

              const loader = await Loading.presentLoading('Declining...');
              const data = {
                username: window.localStorage.username,
                password: window.localStorage.password,
                requisition_id: requisitionId,
                decline_reason: alertData.decline_reason,
              };

              this.$http.post('hr/declineLeaveRequisition', data)
                .then(response => {
                  loader.dismiss();
                  if (response.data.status) {
                    this.presentAlert('Success', response.data.message);
                    this.fetchPendingApprovals();
                  } else {
                    this.presentAlert('Failed', response.data.message);
                  }
                })
                .catch(err => {
                  loader.dismiss();
                });
            }
          }
        ]
      });
      await promptAlert.present();
    }
  }
}
</script>

<style scoped>
/* Core Styles */
.approval-content {
  --background: #f4f6fc;
}

.approval-container {
  padding: 16px;
  max-width: 600px;
  margin: 0 auto;
}

/* Custom Toolbar Styles */
.custom-toolbar {
  --background: linear-gradient(135deg, #102a43 0%, #243b53 100%);
  color: #fff;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.custom-title {
  font-weight: 700;
  font-size: 1.15rem;
  letter-spacing: 0.5px;
}

.refresh-btn {
  --color: #fff;
}

/* Loading state */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 0;
  gap: 12px;
  color: #627d98;
}

/* Pending Summary Banner */
.pending-summary {
  display: flex;
  align-items: center;
  gap: 10px;
  background: #fff8f0;
  border: 1px solid #ffe8cc;
  padding: 12px 16px;
  border-radius: 16px;
  color: #c2410c;
  font-size: 0.86rem;
  margin-bottom: 16px;
}

.summary-icon {
  font-size: 1.25rem;
  color: #ea580c;
  flex-shrink: 0;
}

.pending-summary strong {
  font-weight: 700;
}

/* Approval Card Design */
.approval-card {
  position: relative;
  border-radius: 20px;
  background: #fff;
  box-shadow: 0 10px 30px rgba(16, 42, 67, 0.05);
  border: 1px solid rgba(0, 0, 0, 0.02);
  margin-bottom: 16px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transition: transform 0.2s ease;
}

.approval-card:active {
  transform: scale(0.99);
}

.card-accent {
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  width: 5px;
  background: linear-gradient(180deg, #ea580c 0%, #f97316 100%);
}

/* Header row with avatar */
.card-header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 16px 12px 20px;
  border-bottom: 1px solid #f1f5f9;
}

.employee-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.avatar-badge {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.95rem;
  box-shadow: 0 4px 10px rgba(59, 130, 246, 0.2);
}

.employee-name {
  margin: 0;
  font-size: 0.98rem;
  font-weight: 700;
  color: #0f172a;
}

.employee-sub {
  font-size: 0.75rem;
  color: #64748b;
  font-weight: 500;
}

.leave-type-badge {
  display: flex;
  align-items: center;
  gap: 6px;
  background: #f1f5f9;
  padding: 6px 10px;
  border-radius: 12px;
  font-size: 0.78rem;
  font-weight: 700;
  color: #475569;
}

.type-icon {
  font-size: 1rem;
  color: #64748b;
}

/* Card Body */
.card-body {
  padding: 16px 16px 16px 20px;
}

/* Bento Dates layout */
.dates-bento {
  background: #f8fafc;
  border-radius: 12px;
  padding: 10px 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 1px solid #e2e8f0;
  margin-bottom: 14px;
}

.date-col {
  display: flex;
  flex-direction: column;
}

.date-label {
  font-size: 0.65rem;
  font-weight: 700;
  color: #94a3b8;
  letter-spacing: 0.5px;
}

.date-val {
  font-size: 0.88rem;
  font-weight: 700;
  color: #334e68;
}

.duration-arrow {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  color: #94a3b8;
}

.duration-arrow ion-icon {
  font-size: 1.1rem;
}

.duration-pill {
  background: #e2e8f0;
  color: #475569;
  font-size: 0.68rem;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 8px;
}

/* Reason box */
.reason-box {
  margin-bottom: 14px;
}

.box-title {
  display: block;
  font-size: 0.75rem;
  font-weight: 700;
  color: #94a3b8;
  margin-bottom: 4px;
}

.reason-text {
  margin: 0;
  font-size: 0.88rem;
  color: #334e68;
  font-weight: 500;
  line-height: 1.45;
  background: #fafafa;
  padding: 8px 12px;
  border-radius: 8px;
  border-left: 3px solid #cbd5e1;
}

/* Attachment button */
.attachment-section {
  margin-top: 8px;
}

.btn-attachment {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  padding: 8px 14px;
  border-radius: 10px;
  font-size: 0.82rem;
  font-weight: 600;
  color: #2563eb;
  text-decoration: none;
  transition: background 0.2s;
}

.btn-attachment:hover {
  background: #dbeafe;
}

/* Footer Card Actions */
.card-actions {
  display: flex;
  gap: 10px;
  padding: 12px 16px 16px 20px;
  border-top: 1px solid #f1f5f9;
  background: #fafafa;
}

.btn-action {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border-radius: 12px;
  padding: 12px;
  font-size: 0.88rem;
  font-weight: 700;
  border: none;
  cursor: pointer;
  transition: transform 0.15s;
}

.btn-action:active {
  transform: scale(0.97);
}

.btn-decline {
  background: #fee2e2;
  color: #ef4444;
}

.btn-approve {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: #fff;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.2);
}

/* Empty State Styling */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 24px;
  text-align: center;
  margin-top: 48px;
}

.empty-icon-wrapper {
  background: #d1fae5;
  padding: 20px;
  border-radius: 50%;
  margin-bottom: 16px;
}

.empty-icon {
  font-size: 3rem;
  color: #059669;
}

.empty-state h2 {
  font-size: 1.25rem;
  font-weight: 700;
  color: #102a43;
  margin: 0 0 8px 0;
}

.empty-state p {
  font-size: 0.88rem;
  color: #627d98;
  margin: 0;
  max-width: 300px;
  line-height: 1.5;
}
</style>
